import { AppUsersService } from 'src/app/app-users/services/app-users.service'
import { PointsService } from '../points.service'
import { Component, OnInit } from '@angular/core'
import { BuilderService } from 'src/app/builder-services/builder.service'

@Component({
  selector: 'user',
  templateUrl: './user.component.html',
  styleUrls: ['./user.component.scss']
})
export class UserComponent implements OnInit {
  // ─── Data Arrays ──────────────────────────────────────────────────────────
  allUsers: any[] = []
  filteredUsers: any[] = []
  displayedUsers: any[] = []

  displayedHistory: any[] = []

  // ─── Pagination: Users ────────────────────────────────────────────────────
  userPageIndex = 0
  userPageSize = 10

  // ─── Pagination: History ──────────────────────────────────────────────────
  historyPageIndex = 0
  historyPageSize = 10

  // ─── Other State ─────────────────────────────────────────────────────────
  selectedUser: any
  searchText: string = ''
  toAddPoints: any
  newPoints: any
  project_id = this.builderService.selectedProject

  constructor(
    private builderService: BuilderService,
    private appUsersService: AppUsersService,
    private pointsService: PointsService
  ) {}

  ngOnInit(): void {
    this.getUsers()
  }

  // ─── Pagination Helpers: Users ────────────────────────────────────────────

  updateDisplayedUsers() {
    const start = this.userPageIndex * this.userPageSize
    this.displayedUsers = this.filteredUsers.slice(start, start + this.userPageSize)
  }

  getUserTotalPages(): number {
    return Math.ceil(this.filteredUsers.length / this.userPageSize) || 1
  }

  userPrevPage() {
    if (this.userPageIndex > 0) {
      this.userPageIndex--
      this.updateDisplayedUsers()
    }
  }

  userNextPage() {
    if (this.userPageIndex < this.getUserTotalPages() - 1) {
      this.userPageIndex++
      this.updateDisplayedUsers()
    }
  }

  // ─── Pagination Helpers: History ──────────────────────────────────────────

  updateDisplayedHistory() {
    const data = this.selectedUser?.pointsArray || []
    const start = this.historyPageIndex * this.historyPageSize
    this.displayedHistory = data.slice(start, start + this.historyPageSize)
  }

  getHistoryTotalPages(): number {
    const total = this.selectedUser?.pointsArray?.length || 0
    return Math.ceil(total / this.historyPageSize) || 1
  }

  historyPrevPage() {
    if (this.historyPageIndex > 0) {
      this.historyPageIndex--
      this.updateDisplayedHistory()
    }
  }

  historyNextPage() {
    if (this.historyPageIndex < this.getHistoryTotalPages() - 1) {
      this.historyPageIndex++
      this.updateDisplayedHistory()
    }
  }

  /*
  getUsers
    1- Define the path to the users data in the database using the selected project ID.
    2- Retrieve user data from the database using the defined path.
    3- Log the fetched user data for debugging purposes.
    4- Transform the fetched data into an array of user objects:
      4.1- Iterate over each key in the retrieved data object.
      4.2- For each user:
        4.2.1- Ensure the `points` property is an object. If not, initialize it as an empty object.
        4.2.2- Convert the `points` object into an array by concatenating all the point arrays from the object values.
        4.2.3- Calculate the sum of negative points (redeemed points) using `filter` and `reduce`:
          - Filter points with values less than 0.
          - Sum these negative points.
        4.2.4- Extract the latest `initialDate` from the points array by sorting it in descending order based on `initialDate`.
          - If the points array is empty, set `lastInitialDate` to `null`.
        4.2.5- Create a new user object that includes:
          - All original user data.
          - The calculated sum of redeemed points (`redeem`).
          - The latest initial date (`lastInitialDate`).
        4.2.6- Calculate and set the total points using `calculateTotalPoints`.
    5- Log the transformed user data for debugging purposes.
    6- Assign the transformed data to the `userDataSource` for use in the table.
    7- Handle any errors that occur during data retrieval by logging an error message.
*/
  getUsers(): void {

    this.appUsersService.getProjectUsers().then(
      (data: any[]) => {
        if (!Array.isArray(data)) {
          console.error('Unexpected data format:', data)
          return
        }

        const baseUsers = data.filter((u) => u.email || u.name || u.nickname || u.displayName)
        this.loadUserPointsFromSupabase(baseUsers)
      }
    ).catch(
      (error) => {
        console.error('Error loading user data:', error)
      }
    )
  }

  private loadUserPointsFromSupabase(baseUsers: any[]): void {
    this.pointsService.getAllUserPointsBalances()
      .then((balances: any[]) => {
        const balanceMap = new Map<string, number>()
        ;(balances || []).forEach((row: any) => {
          balanceMap.set(String(row.user_id), row.total_points ?? 0)
        })
        this.allUsers = baseUsers.map((u) => ({
          ...u,
          totalPoints: balanceMap.get(String(u.uid || u.id)) ?? 0
        }))
        this.filteredUsers = [...this.allUsers]
        this.userPageIndex = 0
        this.updateDisplayedUsers()
      })
      .catch((error) => console.error('Error loading user balances:', error))
  }

  /*
    saveChanges
      1- Define the path to the user's points in the database using the selected project ID and user UID.
      2- Define the path to the validity date in the database.
      3- Fetch the validity date from the database.
      4- Check if `newTransaction` and its `initialDate` are present. If not, exit the function.
      5- Parse the `initialDate` from the `newTransaction`.
      6- If `validityData` is available, calculate the `expiryDate` by adding the validity period to the `initialDate`.
      7- Create a `transaction` object that includes `initialDate`, `points`, `isExpired`, and `expiryDate`.
      8- Log the transaction for debugging purposes.
      9- Generate a unique key based on the current timestamp.
      10- Initialize the points array for the user if it doesn't exist.
      11- Push the new transaction into the user's points.
      12- Update the `pointsArray` for the selected user.
      13- Reset the transaction form state.
      14- Update the user's points in the database.
      15- Recalculate the user's total points after adding the transaction.
      16- Handle any errors that occur during the process by logging an error message.
  */

  saveChanges(user: any) {
    this.pointsService.getPointsSetups().subscribe((setups: any[]) => {
        const validityDays = (setups && setups[0]?.validity_date) ?? 30

        if (!this.newPoints || !this.newPoints.points) {
          this.toAddPoints = false
          return
        }

        const currentDate = new Date()
        currentDate.setHours(0, 0, 0, 0)
        const expiryDate = new Date(currentDate)
        expiryDate.setDate(expiryDate.getDate() + Number(validityDays))

        const userId = user.uid || user.id
        const ledgerRow: Record<string, any> = {
          project_id: Number(this.project_id),
          user_id: userId,
          source_type: 'earn_manual',
          points: Number(this.newPoints.points),
          initial_date: currentDate.toISOString(),
          expiry_date: expiryDate.toISOString(),
          is_expired: false
        }

        this.pointsService.insertPointsLedgerEntries([ledgerRow])
          .then(() => {

            const transaction = {
              expiryDate: expiryDate.toLocaleDateString(),
              initialDate: currentDate.toLocaleDateString(),
              isExpired: false,
              points: this.newPoints.points
            }

            if (!this.selectedUser.points) this.selectedUser.points = {}
            const key = Date.now().toString()
            this.selectedUser.points[key] = [transaction]

            this.pointsArray()
            this.toAddPoints = false

            // Refresh balance from trigger-maintained table
            this.pointsService.getUserPointsBalance(userId)
              .then((total) => {
                user.totalPoints = total ?? 0
              })
          })
      })
  }

  /*
  showUserHistory
    1- Check if the clicked user is already selected. If so, deselect the user by setting `selectedUser` to `null`.
    2- If a different user is clicked, select the new user and assign them to `selectedUser`.
    3- Generate the `pointsArray` for the selected user by flattening their points into an array.
    4- Wrap `selectedUser.pointsArray` in a `MatTableDataSource` to enable table features like pagination and sorting.
    5- Assign the paginator (`paginator2`) to `pointsDataSource` to enable pagination for the second table.
*/

  showUserHistory(element: any) {
    if (this.selectedUser === element) {
      this.selectedUser = null
      this.displayedHistory = []
    } else {
      this.selectedUser = element
      this.loadUserLedger(element)
    }
  }

  private loadUserLedger(user: any): void {
    const userId = user.uid || user.id
    this.pointsService.getUserPointsLedger(userId)
      .then((data: any[]) => {
        const ledgerRows = (data || []).slice().sort(
          (a: any, b: any) => new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime(),
        )
        if (!this.selectedUser.points) this.selectedUser.points = {}
        ledgerRows.forEach((row: any) => {
          const key = row.id
          this.selectedUser.points[key] = [{
            initialDate: row.initial_date ? new Date(row.initial_date).toLocaleDateString() : '',
            expiryDate: row.expiry_date ? new Date(row.expiry_date).toLocaleDateString() : '',
            isExpired: row.is_expired ?? false,
            points: row.points,
            source_type: row.source_type
          }]
        })
        this.pointsArray()
        this.historyPageIndex = 0
        this.updateDisplayedHistory()
      })
      .catch((error) => console.error('Error loading user ledger:', error))
  }

  /*
    pointsArray
      1- Initialize an empty array to hold all points.
      2- Iterate over each key in the selected user's points object.
      3- For each point, push it into the `pointsArray`.
      4- Assign the populated array to `selectedUser.pointsArray` for display.
  */

  pointsArray() {
    const pointsArray: any[] = []
    Object.keys(this.selectedUser.points).forEach((key) => {
      this.selectedUser.points[key].forEach((point: any) => {
        pointsArray.push(point)
      })
    })

    this.selectedUser.pointsArray = pointsArray
    this.updateDisplayedHistory()
  }

    /*
    addTransaction
      1- Set the flag to indicate that a new transaction is being added.
      2- Initialize the `newTransaction` empty object.
  */

  addPoints() {
    this.toAddPoints = true
    this.newPoints = {}
  }

  /*
    calculateTotalPoints
      1- Define the path to the user's total points in the database using the selected project ID and user UID.
      2- Retrieve the points object from the user data. If not present, initialize it as an empty object.
      3- Flatten the points from the object into a single array.
      4- Calculate the total points by summing all point values.
      5- Assign the calculated total to the user's `totalPoints` property.
      6- Update the `totalPoints` in the database at the defined path.
      7- Handle the success and error responses by logging appropriate messages.
  */

  calculateTotalPoints(user: any): void {
    const userId = user.uid || user.id
    this.pointsService.getUserPointsBalance(userId)
      .then((total) => {
        user.totalPoints = total ?? 0
      })
      .catch((error) => console.error('Error fetching total points:', error))
  }

  /*
  applyFilter
    1- Trim and convert the search text to lowercase.
    2- Define a `filterPredicate` to match users based on `displayName`, `name`, `uid`, or `email`.
    3- Apply the filter to the `userDataSource` using the processed search text.
*/

  applyFilter(): void {
    const filterValue = this.searchText ? this.searchText.trim().toLowerCase() : ''

    if (!filterValue) {
      this.filteredUsers = [...this.allUsers]
    } else {
      this.filteredUsers = this.allUsers.filter((data: any) =>
        data.displayName?.toLowerCase().includes(filterValue) ||
        data.name?.toLowerCase().includes(filterValue) ||
        data.nickname?.toLowerCase().includes(filterValue) ||
        data.email?.toLowerCase().includes(filterValue)
      )
    }

    this.userPageIndex = 0
    this.updateDisplayedUsers()
  }

    /*
  deletePoint
    1- Define the path to the user's points in the database using the selected project ID and user UID.
    2- Receive the `pointId` of the point to be deleted.
    3- Iterate through the user's points object to find the point with the matching `pointId`.
    4- Confirm if the user wants to delete the point with the found value.
    5- If confirmed, remove the specific point from the user's points object.
    6- Update the pointsArray to reflect the changes.
    7- Update the user's points in the database using the updated points object.
    8- Recalculate the user's total points after the deletion.
    9- Handle any errors that occur during the process by logging an error message.
*/
  deletePoint(user: any, pointId: string): void {
    const pointsForKey: any[] = user.points?.[pointId]
    if (!pointsForKey || pointsForKey.length === 0) {
      console.error(`Point with ID: ${pointId} not found.`)
      return
    }

    const pointToDelete = pointsForKey[0]
    const confirmDeletion = window.confirm(`Are you sure you want to delete the point with value ${pointToDelete.points}?`)
    if (!confirmDeletion) return

    delete user.points[pointId]
    this.pointsArray()

    const userId = user.uid || user.id

    this.pointsService.insertPointsLedgerEntries([{
      user_id: userId,
      source_type: 'adjustment',
      points: -(pointToDelete.points || 0),
      initial_date: new Date().toISOString(),
      is_expired: false
    }]).then(() => {
      this.calculateTotalPoints(user)
    })
  }
}
